import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { usePreferences } from '../contexts/PreferencesContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Settings, Globe, Coins, User, Mail, Shield, Check, Lock } from 'lucide-react';
import { toast } from 'sonner';

const SettingsPage = () => {
  const { user, changePassword } = useAuth();
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [passwordLoading, setPasswordLoading] = useState(false);

  const openPasswordDialog = () => {
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setPasswordError('');
    setPasswordDialogOpen(true);
  };

  const handlePasswordChange = async () => {
    setPasswordError('');
    if (newPassword.length < 6) {
      setPasswordError('Le nouveau mot de passe doit contenir au moins 6 caractères.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordError('Les mots de passe ne correspondent pas.');
      return;
    }
    setPasswordLoading(true);
    try {
      await changePassword(currentPassword, newPassword);
      toast.success('Mot de passe modifié avec succès');
      setPasswordDialogOpen(false);
    } catch (error) {
      const detail = error?.response?.data?.detail;
      setPasswordError(detail || 'Erreur lors du changement de mot de passe.');
    } finally {
      setPasswordLoading(false);
    }
  };

  const {
    language, 
    currency, 
    currencies, 
    languages, 
    updateLanguage, 
    updateCurrency,
    loading,
    t 
  } = usePreferences();

  const handleLanguageChange = async (newLanguage) => {
    const success = await updateLanguage(newLanguage);
    if (success) {
      toast.success(language === 'fr' ? 'Langue mise à jour' : 'Language updated');
    } else {
      toast.error(language === 'fr' ? 'Erreur lors de la mise à jour' : 'Update failed');
    }
  };

  const handleCurrencyChange = async (newCurrency) => {
    const success = await updateCurrency(newCurrency);
    if (success) {
      toast.success(language === 'fr' ? 'Devise mise à jour' : 'Currency updated');
    } else {
      toast.error(language === 'fr' ? 'Erreur lors de la mise à jour' : 'Update failed');
    }
  };

  return (
    <div className="space-y-6 animate-fade-in max-w-3xl" data-testid="settings-page">
      <div>
        <h1 className="font-heading text-2xl font-bold text-slate-900 tracking-tight flex items-center gap-2">
          <Settings className="w-7 h-7 text-blue-600" />
          {t('settings.title')}
        </h1>
        <p className="text-slate-500 mt-1">{t('settings.subtitle')}</p>
      </div>

      {/* User Profile Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <User className="w-5 h-5 text-slate-400" />
            {t('settings.profile')}
          </CardTitle>
          <CardDescription>
            {language === 'fr' ? 'Informations de votre compte' : 'Your account information'}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1">
              <Label className="text-sm text-slate-500">
                {language === 'fr' ? 'Nom complet' : 'Full name'}
              </Label>
              <p className="font-medium">{user?.prenom} {user?.nom}</p>
            </div>
            <div className="space-y-1">
              <Label className="text-sm text-slate-500 flex items-center gap-1">
                <Mail className="w-3 h-3" />
                {t('auth.email')}
              </Label>
              <p className="font-medium">{user?.email}</p>
            </div>
            <div className="space-y-1">
              <Label className="text-sm text-slate-500 flex items-center gap-1">
                <Shield className="w-3 h-3" />
                {language === 'fr' ? 'Rôle' : 'Role'}
              </Label>
              <p className="font-medium capitalize">{user?.role}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Preferences Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Globe className="w-5 h-5 text-blue-500" />
            {t('settings.preferences')}
          </CardTitle>
          <CardDescription>
            {language === 'fr' 
              ? 'Personnalisez l\'affichage de l\'application' 
              : 'Customize the application display'}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Language Selection */}
          <div className="space-y-2">
            <Label htmlFor="language" className="flex items-center gap-2">
              <Globe className="w-4 h-4 text-slate-400" />
              {t('settings.language')}
            </Label>
            <Select 
              value={language} 
              onValueChange={handleLanguageChange}
              disabled={loading}
            >
              <SelectTrigger id="language" className="w-full md:w-64" data-testid="language-select">
                <SelectValue placeholder={t('settings.selectLanguage')} />
              </SelectTrigger>
              <SelectContent>
                {languages.map((lang) => (
                  <SelectItem key={lang.code} value={lang.code}>
                    <span className="flex items-center gap-2">
                      {lang.code === 'fr' ? '🇫🇷' : '🇬🇧'} {lang.name}
                      {lang.code === language && <Check className="w-4 h-4 text-green-500 ml-2" />}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-sm text-slate-500">
              {language === 'fr' 
                ? 'La langue de l\'interface utilisateur' 
                : 'The user interface language'}
            </p>
          </div>

          {/* Currency Selection */}
          <div className="space-y-2">
            <Label htmlFor="currency" className="flex items-center gap-2">
              <Coins className="w-4 h-4 text-slate-400" />
              {t('settings.currency')}
            </Label>
            <Select 
              value={currency} 
              onValueChange={handleCurrencyChange}
              disabled={loading}
            >
              <SelectTrigger id="currency" className="w-full md:w-64" data-testid="currency-select">
                <SelectValue placeholder={t('settings.selectCurrency')} />
              </SelectTrigger>
              <SelectContent>
                {currencies.map((curr) => (
                  <SelectItem key={curr.code} value={curr.code}>
                    <span className="flex items-center gap-2">
                      <span className="font-mono text-sm bg-slate-100 px-1.5 py-0.5 rounded">
                        {curr.symbol}
                      </span>
                      {curr.name} ({curr.code})
                      {curr.code === currency && <Check className="w-4 h-4 text-green-500 ml-2" />}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-sm text-slate-500">
              {language === 'fr' 
                ? 'Les montants seront convertis automatiquement' 
                : 'Amounts will be converted automatically'}
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Security Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Lock className="w-5 h-5 text-slate-400" />
            {language === 'fr' ? 'Sécurité' : 'Security'}
          </CardTitle>
          <CardDescription>
            {language === 'fr' ? 'Gérez la sécurité de votre compte' : 'Manage your account security'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <p className="font-medium text-sm">
                {language === 'fr' ? 'Mot de passe' : 'Password'}
              </p>
              <p className="text-sm text-slate-500">
                {language === 'fr' ? 'Modifiez votre mot de passe de connexion' : 'Change your login password'}
              </p>
            </div>
            <Button variant="outline" onClick={openPasswordDialog}>
              <Lock className="w-4 h-4 mr-2" />
              {language === 'fr' ? 'Changer le mot de passe' : 'Change password'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Password Change Dialog */}
      <Dialog open={passwordDialogOpen} onOpenChange={setPasswordDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {language === 'fr' ? 'Changer le mot de passe' : 'Change password'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {passwordError && (
              <Alert variant="destructive">
                <AlertDescription>{passwordError}</AlertDescription>
              </Alert>
            )}
            <div className="space-y-2">
              <Label htmlFor="current-password">
                {language === 'fr' ? 'Mot de passe actuel' : 'Current password'}
              </Label>
              <Input
                id="current-password"
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                autoComplete="current-password"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="new-password">
                {language === 'fr' ? 'Nouveau mot de passe' : 'New password'}
              </Label>
              <Input
                id="new-password"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                autoComplete="new-password"
              />
              <p className="text-xs text-slate-500">
                {language === 'fr' ? 'Au moins 6 caractères' : 'At least 6 characters'}
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirm-password">
                {language === 'fr' ? 'Confirmer le nouveau mot de passe' : 'Confirm new password'}
              </Label>
              <Input
                id="confirm-password"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                autoComplete="new-password"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPasswordDialogOpen(false)} disabled={passwordLoading}>
              {language === 'fr' ? 'Annuler' : 'Cancel'}
            </Button>
            <Button onClick={handlePasswordChange} disabled={passwordLoading}>
              {passwordLoading
                ? (language === 'fr' ? 'Modification...' : 'Updating...')
                : (language === 'fr' ? 'Modifier le mot de passe' : 'Update password')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Exchange Rates Info Card */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="pt-6">
          <div className="flex items-start gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Coins className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h3 className="font-medium text-blue-900">
                {language === 'fr' ? 'Taux de change' : 'Exchange rates'}
              </h3>
              <p className="text-sm text-blue-700 mt-1">
                {language === 'fr' 
                  ? 'Les taux de change utilisés pour la conversion :'
                  : 'Exchange rates used for conversion:'}
              </p>
              <div className="mt-2 flex flex-wrap gap-3">
                <span className="text-sm bg-white px-2 py-1 rounded border border-blue-200">
                  1 EUR = 1.08 USD
                </span>
                <span className="text-sm bg-white px-2 py-1 rounded border border-blue-200">
                  1 EUR = 655.957 XOF
                </span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SettingsPage;
